import { useState } from "react";
import SectionHeading from "@/components/feature/SectionHeading";
import { commentItems } from "@/mocks/home";

type CommentItemType = (typeof commentItems)[number];

function CommentCard({ item }: { item: CommentItemType }) {
  const [liked, setLiked] = useState(false);
  const likeCount = item.likes + (liked ? 1 : 0);

  return (
    <article className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-secondary-300 md:p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-900">
          {item.initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium text-foreground-900">{item.name}</span>
            <span className="text-xs text-foreground-400">{item.time}</span>
            <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
              {item.scene}
            </span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.text}</p>
          <div className="mt-3 flex items-center gap-5 text-xs text-foreground-500">
            <button
              type="button"
              onClick={() => setLiked((prev) => !prev)}
              className={
                liked
                  ? "flex cursor-pointer items-center gap-1 text-primary-600"
                  : "flex cursor-pointer items-center gap-1 transition-colors hover:text-primary-600"
              }
            >
              <i className={liked ? "ri-thumb-up-fill text-sm" : "ri-thumb-up-line text-sm"}></i>
              {likeCount}
            </button>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line text-sm"></i>
              {item.replies}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function CommentVibe() {
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState("");
  const spotlight = commentItems[0];
  const restComments = commentItems.slice(1);

  const handlePost = () => {
    if (!draft.trim()) {
      setNote("先写两句再发，现场动向需要你来补");
      return;
    }
    setNote("演示站点：这段内容不会真正发布，仅作体验");
    setDraft("");
  };

  return (
    <section id="scene" className="w-full bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="吃瓜现场"
          title="评论区比正片还热闹"
          description="同一条更新，有人看原速、有人开慢放、有人只对时间线。看看别人的当场判断，再回头对一遍画面，比一个人闷着看更清楚。"
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3 md:mt-10">
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 flex flex-col gap-5">
              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
                <div className="flex items-center gap-2 text-xs font-medium text-accent-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 text-background-50">
                    <i className="ri-chat-quote-line text-sm"></i>
                  </span>
                  热评置顶 · {spotlight.scene}
                </div>
                <p className="mt-4 font-heading text-lg font-semibold leading-relaxed text-foreground-950">
                  “{spotlight.text}”
                </p>
                <div className="mt-4 flex items-center justify-between text-xs text-foreground-600">
                  <span>{spotlight.name} · {spotlight.time}</span>
                  <span className="flex items-center gap-1 text-primary-600">
                    <i className="ri-thumb-up-fill text-sm"></i>
                    {spotlight.likes}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950">
                  说说你看到的当场动向
                </h3>
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value.slice(0, 500))}
                  rows={3}
                  placeholder="比如：第二段走位和第一段明显不一样……"
                  className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] text-foreground-400">{draft.length}/500</span>
                  <button
                    type="button"
                    onClick={handlePost}
                    className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line"></i>
                    发布看法
                  </button>
                </div>
                {note && <p className="mt-3 text-xs text-foreground-500">{note}</p>}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 lg:col-span-2">
            {restComments.map((item) => (
              <CommentCard key={item.id} item={item} />
            ))}
            <a
              href="#latest"
              className="mt-1 flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              查看全部 4,281 条现场评论
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}